import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import PageLayout from '../components/PageLayout';
import PageHeader from '../components/PageHeader';
import { FormSection, InputField, SelectField, TextAreaField } from '../components/ListingForm';
import { useFormatCurrency, useAppConfig } from '../config/useAppConfig';
import propertiesService from '../services/propertiesService';
import usersService from '../services/usersService';
import bookingsService from '../services/bookingsService';

export default function CreateBooking() {
  const navigate = useNavigate();
  const formatCurrency = useFormatCurrency();
  const { config } = useAppConfig();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const queryPropertyId = searchParams.get('propertyId');
  const queryCheckIn = searchParams.get('checkIn');
  
  const [guestCount, setGuestCount] = useState(2);
  const [insuranceEnabled, setInsuranceEnabled] = useState(true);
  const [selectedGuest, setSelectedGuest] = useState('');
  const [selectedGuestId, setSelectedGuestId] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(queryPropertyId || '');
  const [checkInDate, setCheckInDate] = useState(queryCheckIn || '');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [bookingSource, setBookingSource] = useState('direct');
  const [specialRequests, setSpecialRequests] = useState('');
  const [discountType, setDiscountType] = useState('none');
  const [discountValue, setDiscountValue] = useState('');
  
  const [properties, setProperties] = useState([]);
  const [pricingBreakdown, setPricingBreakdown] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [checkInMonth, setCheckInMonth] = useState(new Date());
  const [checkOutMonth, setCheckOutMonth] = useState(new Date());
  const [bookedRanges, setBookedRanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isEditMode = id && id !== 'undefined';
  const pageTitle = isEditMode ? 'Edit Booking' : 'Create New Booking';

  // Fetch properties on mount
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const data = await propertiesService.fetchProperties();
        setProperties(data);
      } catch (err) {
        console.error('Failed to fetch properties:', err);
      }
    };
    fetchProperties();
  }, []);

  // Fetch pricing breakdown when property + dates are all set
  useEffect(() => {
    if (!selectedProperty || !checkInDate || !checkOutDate) {
      setPricingBreakdown(null);
      return;
    }
    propertiesService.calculatePrice(selectedProperty, checkInDate, checkOutDate)
      .then(setPricingBreakdown)
      .catch(() => setPricingBreakdown(null));
  }, [selectedProperty, checkInDate, checkOutDate]);

  // Fetch booked ranges when selected property changes
  useEffect(() => {
    if (!selectedProperty) {
      setBookedRanges([]);
      return;
    }
    bookingsService.getAvailability(selectedProperty)
      .then(setBookedRanges)
      .catch(() => setBookedRanges([]));
  }, [selectedProperty]);

  // Search users when query changes
  useEffect(() => {
    if (userSearchQuery && userSearchQuery.length >= 2) {
      const searchUsers = async () => {
        try {
          const data = await usersService.fetchUsers({ search: userSearchQuery });
          setFilteredUsers(data.users || []);
          setShowUserDropdown(true);
        } catch (err) {
          console.error('Failed to search users:', err);
        }
      };
      const timer = setTimeout(searchUsers, 500);
      return () => clearTimeout(timer);
    } else {
      setFilteredUsers([]);
      setShowUserDropdown(false);
    }
  }, [userSearchQuery]);

  const handleUserSelect = (user) => {
    setSelectedGuestId(user.id);
    setSelectedGuest(`${user.firstName} ${user.lastName}`);
    setUserSearchQuery('');
    setShowUserDropdown(false);
  };

  const toDateTs = (d) => {
    const dateOnly = typeof d === 'string' && d.includes('T') ? d.split('T')[0] : d;
    return new Date(dateOnly + 'T00:00:00').getTime();
  };

  const isDateBooked = (dateString) => {
    const ts = toDateTs(dateString);
    return bookedRanges.some(({ checkInDate, checkOutDate }) => {
      const start = toDateTs(checkInDate);
      const end = toDateTs(checkOutDate);
      return ts >= start && ts < end;
    });
  };

  const navigateMonth = (type, direction) => {
    if (type === 'checkin') {
      const newMonth = new Date(checkInMonth);
      newMonth.setMonth(newMonth.getMonth() + (direction === 'prev' ? -1 : 1));
      setCheckInMonth(newMonth);
    } else {
      const newMonth = new Date(checkOutMonth);
      newMonth.setMonth(newMonth.getMonth() + (direction === 'prev' ? -1 : 1));
      setCheckOutMonth(newMonth);
    }
  };

  const generateCalendarDays = (type) => {
    const currentDate = type === 'checkin' ? checkInMonth : checkOutMonth;
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    const days = [];

    const ciTs = checkInDate ? new Date(checkInDate + 'T00:00:00').getTime() : null;
    const coTs = checkOutDate ? new Date(checkOutDate + 'T00:00:00').getTime() : null;

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${type}-${i}`} className="p-2"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const ts = new Date(dateString + 'T00:00:00').getTime();
      const isBooked = isDateBooked(dateString);
      const isInvalidCheckout = type === 'checkout' && ciTs && ts <= ciTs;
      const isSelected = (type === 'checkin' ? checkInDate : checkOutDate) === dateString;
      const isInRange = !isSelected && ciTs && coTs && ts > ciTs && ts < coTs;

      days.push(
        <div
          key={`${type}-${day}`}
          className={`p-2 rounded-lg text-sm font-medium transition-colors text-center ${
            isBooked ? 'bg-destructive text-destructive-foreground cursor-not-allowed' :
            isInvalidCheckout ? 'bg-muted/30 text-muted-foreground cursor-not-allowed' :
            isSelected ? 'bg-primary text-primary-foreground' :
            isInRange ? 'bg-primary/20 text-foreground cursor-pointer' :
            'hover:bg-muted text-foreground cursor-pointer'
          }`}
          onClick={() => {
            if (isBooked || isInvalidCheckout) return;
            if (type === 'checkin') {
              setCheckInDate(dateString);
              if (checkOutDate && new Date(checkOutDate + 'T00:00:00') <= new Date(dateString + 'T00:00:00')) {
                setCheckOutDate('');
              }
            } else if (type === 'checkout') {
              if (checkInDate && new Date(dateString + 'T00:00:00') > new Date(checkInDate + 'T00:00:00')) {
                setCheckOutDate(dateString);
              }
            }
          }}
        >
          {day}
        </div>
      );
    }
    return days;
  };

  const getNights = () => checkInDate && checkOutDate
    ? Math.ceil((new Date(checkOutDate) - new Date(checkInDate)) / (1000 * 60 * 60 * 24))
    : 0;

  const getNightlyRate = () => {
    if (pricingBreakdown?.nightlyRate != null) return Number(pricingBreakdown.nightlyRate);
    const prop = properties.find(p => String(p.id) === String(selectedProperty));
    return parseFloat(prop?.pricePerNight) || 0;
  };

  const getSubtotal = () => {
    if (pricingBreakdown?.subtotal != null) return Number(pricingBreakdown.subtotal);
    return getNightlyRate() * getNights();
  };

  const getSecurityDeposit = () => {
    const prop = properties.find(p => String(p.id) === String(selectedProperty));
    return parseFloat(prop?.securityDeposit) || 0;
  };

  const getGrandTotalBeforeDiscount = () => {
    const subtotal = getSubtotal();
    const insurance = insuranceEnabled ? 4500 : 0;
    const vat = Math.round(subtotal * (config.tax.vatRate / 100));
    return subtotal + vat + insurance + getSecurityDeposit();
  };

  const getDiscountAmount = () => {
    const grandTotal = getGrandTotalBeforeDiscount();
    const value = parseFloat(discountValue) || 0;
    let amount = 0;
    if (discountType === 'percentage') amount = grandTotal * (value / 100);
    else if (discountType === 'fixed') amount = value;
    return Math.max(0, Math.min(amount, grandTotal));
  };

  const calculateTotal = () => {
    return getGrandTotalBeforeDiscount() - getDiscountAmount();
  };

  const handleConfirm = async () => {
    if (!selectedGuestId) {
      setError('Please search and select a guest from the dropdown list.');
      return;
    }
    if (!selectedProperty) {
      setError('Please select a property.');
      return;
    }
    if (!checkInDate || !checkOutDate) {
      setError('Please select both check-in and check-out dates.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const vatAmount = Math.round(getSubtotal() * (config.tax.vatRate / 100));

      const payload = {
        userId: selectedGuestId,
        propertyId: selectedProperty,
        checkInDate: checkInDate,
        checkOutDate: checkOutDate,
        numberOfGuests: guestCount,
        notes: specialRequests,
        status: 'confirmed',
        paymentStatus: 'paid',
        vat: vatAmount,
        cautionFee: getSecurityDeposit(),
        discountType,
        discountValue: parseFloat(discountValue) || 0,
      };

      if (isEditMode) {
        // await bookingsService.updateBooking(id, payload);
      } else {
        await bookingsService.createBooking(payload);
      }
      navigate('/bookings');
    } catch (err) {
      const data = err.response?.data;
      const errorMessage = data?.error || (data?.errors && data.errors.map(e => e.message).join(', ')) || data?.message || err.message || 'Failed to create booking';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <PageHeader
        title={pageTitle}
        description={isEditMode ? 'Modify booking details and manage reservation.' : 'Reserve a listing for a guest manually.'}
        backButton={{
          to: '/bookings',
          icon: 'lucide:arrow-left',
          ariaLabel: 'Back to bookings'
        }}
        actions={[
          {
            type: 'button',
            label: 'Cancel',
            onClick: () => navigate('/bookings')
          },
          {
            type: 'button',
            label: loading ? 'Processing...' : (isEditMode ? 'Update Booking' : 'Confirm Reservation'),
            variant: 'primary',
            onClick: handleConfirm,
            disabled: loading
          }
        ]}
      />

      <div className="flex-1 overflow-y-auto p-8">
        {error && (
          <div className="max-w-5xl mx-auto mb-6 bg-destructive/10 text-destructive p-4 rounded-xl text-sm font-bold">
            {error}
          </div>
        )}
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          <div className="lg:col-span-2 space-y-6">
            <FormSection title="Guest Information" icon="lucide:user">
              <div className="space-y-2 relative">
                <InputField 
                  label="Search Guest"
                  placeholder="Type name or email (min 3 chars)..."
                  value={userSearchQuery || selectedGuest}
                  onChange={(e) => {
                    setUserSearchQuery(e.target.value);
                    if (!e.target.value) {
                      setSelectedGuest('');
                      setSelectedGuestId('');
                    }
                  }}
                  onFocus={() => setShowUserDropdown(true)}
                  icon="lucide:search"
                />
                
                {showUserDropdown && filteredUsers.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-lg z-50 max-h-60 overflow-y-auto">
                    {filteredUsers.map(user => (
                      <div
                        key={user.id}
                        onClick={() => handleUserSelect(user)}
                        className="p-3 hover:bg-muted cursor-pointer border-b border-border last:border-b-0"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                            <Icon icon="lucide:user" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-bold">{user.firstName} {user.lastName}</p>
                            <p className="text-xs text-muted-foreground">{user.email}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </FormSection>

            <FormSection title="Property & Dates" icon="lucide:house">
              <div className="space-y-4">
                <SelectField 
                  label="Select Property"
                  value={selectedProperty}
                  onChange={(e) => setSelectedProperty(e.target.value)}
                >
                  <option value="">Select a property...</option>
                  {properties.map(listing => (
                    <option key={listing.id} value={listing.id}>
                      {listing.name} - {listing.city}
                    </option>
                  ))}
                </SelectField>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase text-muted-foreground tracking-wider">Check-in</label>
                    <div className="bg-card p-4 rounded-xl border border-border">
                      <div className="flex items-center justify-between mb-4">
                        <button onClick={() => navigateMonth('checkin', 'prev')} className="p-2 hover:bg-muted rounded-lg transition-colors">
                          <Icon icon="lucide:chevron-left" className="text-lg" />
                        </button>
                        <span className="text-sm font-bold text-muted-foreground">
                          {checkInMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </span>
                        <button onClick={() => navigateMonth('checkin', 'next')} className="p-2 hover:bg-muted rounded-lg transition-colors">
                          <Icon icon="lucide:chevron-right" className="text-lg" />
                        </button>
                      </div>
                      <div className="grid grid-cols-7 gap-1 text-center mb-4">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                          <div key={i} className="text-xs font-bold text-muted-foreground">{d}</div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-1 text-center">
                        {generateCalendarDays('checkin')}
                      </div>
                      <div className="mt-2 p-2 bg-muted/50 rounded-lg">
                        <div className="flex items-center justify-center gap-2 text-xs">
                          <div className="w-3 h-3 bg-primary rounded-full"></div>
                          <span className="text-muted-foreground">Selected</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase text-muted-foreground tracking-wider">Check-out</label>
                    <div className="bg-card p-4 rounded-xl border border-border">
                      <div className="flex items-center justify-between mb-4">
                        <button onClick={() => navigateMonth('checkout', 'prev')} className="p-2 hover:bg-muted rounded-lg transition-colors">
                          <Icon icon="lucide:chevron-left" className="text-lg" />
                        </button>
                        <span className="text-sm font-bold text-muted-foreground">
                          {checkOutMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </span>
                        <button onClick={() => navigateMonth('checkout', 'next')} className="p-2 hover:bg-muted rounded-lg transition-colors">
                          <Icon icon="lucide:chevron-right" className="text-lg" />
                        </button>
                      </div>
                      <div className="grid grid-cols-7 gap-1 text-center mb-4">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                          <div key={i} className="text-xs font-bold text-muted-foreground">{d}</div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-1 text-center">
                        {generateCalendarDays('checkout')}
                      </div>
                      <div className="mt-2 p-2 bg-muted/50 rounded-lg">
                        <div className="flex items-center justify-center gap-2 text-xs flex-wrap">
                          <div className="w-3 h-3 bg-primary rounded-full"></div>
                          <span className="text-muted-foreground">Selected</span>
                          {checkInDate && (
                            <>
                              <div className="w-3 h-3 bg-muted/30 rounded-full border border-border"></div>
                              <span className="text-muted-foreground">Unavailable</span>
                            </>
                          )}
                        </div>
                        {checkInDate && (
                          <p className="text-xs text-muted-foreground text-center mt-1">
                            Checkout must be after {new Date(checkInDate + 'T00:00:00').toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase text-muted-foreground tracking-wider">Number of Guests</label>
                    <div className="flex items-center gap-3 bg-muted rounded-xl p-1">
                      <button 
                        onClick={() => setGuestCount(Math.max(1, guestCount - 1))}
                        className="w-8 h-8 flex items-center justify-center bg-card rounded-lg shadow-sm hover:bg-muted transition-colors"
                      >
                        <Icon icon="lucide:minus" />
                      </button>
                      <span className="flex-1 text-center font-bold">{guestCount}</span>
                      <button 
                        onClick={() => setGuestCount(guestCount + 1)}
                        className="w-8 h-8 flex items-center justify-center bg-card rounded-lg shadow-sm hover:bg-muted transition-colors"
                      >
                        <Icon icon="lucide:plus" />
                      </button>
                    </div>
                  </div>
                  <SelectField 
                    label="Booking Source"
                    value={bookingSource}
                    onChange={(e) => setBookingSource(e.target.value)}
                  >
                    <option value="direct">Direct Admin Entry</option>
                    <option value="phone">Phone Inquiry</option>
                    <option value="walkin">Walk-in</option>
                  </SelectField>
                </div>
              </div>
            </FormSection>

            <FormSection title="Additional Details" icon="lucide:zap">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl border border-border/50">
                  <div className="flex items-center gap-3">
                    <Icon icon="lucide:shield-check" className="text-tertiary text-xl" />
                    <div>
                      <p className="text-sm font-bold">Include Insurance</p>
                      <p className="text-xs text-muted-foreground">Comprehensive damage protection plan</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setInsuranceEnabled(!insuranceEnabled)}
                    className={`w-12 h-6 rounded-full relative transition-colors ${
                      insuranceEnabled ? 'bg-tertiary' : 'bg-muted-foreground/30'
                    }`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${insuranceEnabled ? 'right-1' : 'left-1'}`}></div>
                  </button>
                </div>
                <TextAreaField
                  label="Special Requests / Notes"
                  placeholder="Add any specific guest requirements..."
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  rows="4"
                />
              </div>
            </FormSection>

          </div>

          <div className="space-y-6">
            <div className="bg-card p-6 rounded-2xl border border-border shadow-lg sticky top-28">
              <h2 className="text-lg font-heading font-bold mb-6">Booking Summary</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <SelectField
                  label="Discount Type"
                  value={discountType}
                  onChange={(e) => {
                    setDiscountType(e.target.value);
                    if (e.target.value === 'none') setDiscountValue('');
                  }}
                >
                  <option value="none">No Discount</option>
                  <option value="fixed">Fixed Amount</option>
                  <option value="percentage">Percentage</option>
                </SelectField>
                {discountType !== 'none' && (
                  <InputField
                    label={discountType === 'percentage' ? 'Discount (%)' : 'Discount Amount'}
                    type="number"
                    placeholder={discountType === 'percentage' ? 'e.g. 10' : 'e.g. 5000'}
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                  />
                )}
              </div>
              
              <div className="space-y-4 mb-8">
                {pricingBreakdown?.baseNights > 0 && pricingBreakdown?.ruleNights > 0 ? (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Standard rate</span>
                      <span className="font-bold">{formatCurrency(pricingBreakdown.basePrice)} × {pricingBreakdown.baseNights} nights</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-tertiary">{pricingBreakdown.appliedRule.label}</span>
                      <span className="font-bold">{formatCurrency(pricingBreakdown.appliedRule.price)} × {pricingBreakdown.ruleNights} nights</span>
                    </div>
                  </>
                ) : pricingBreakdown?.appliedRule ? (
                  <div className="flex justify-between text-sm">
                    <span className="text-tertiary">{pricingBreakdown.appliedRule.label}</span>
                    <span className="font-bold">
                      {formatCurrency(getNightlyRate())} × {getNights()} nights
                    </span>
                  </div>
                ) : (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Nightly Rate</span>
                    <span className="font-bold">
                      {formatCurrency(getNightlyRate())} × {getNights()} nights
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">VAT {config.tax.vatRate}%</span>
                  <span className="font-bold">
                    {formatCurrency(Math.round(getSubtotal() * (config.tax.vatRate / 100)))}
                  </span>
                </div>
                {getSecurityDeposit() > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Security Deposit</span>
                    <span className="font-bold">{formatCurrency(getSecurityDeposit())}</span>
                  </div>
                )}
                {insuranceEnabled && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Insurance</span>
                    <span className="font-bold text-tertiary">+{formatCurrency(4500)}</span>
                  </div>
                )}
                {getDiscountAmount() > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Discount {discountType === 'percentage' ? `(${discountValue || 0}%)` : ''}
                    </span>
                    <span className="font-bold text-destructive">-{formatCurrency(getDiscountAmount())}</span>
                  </div>
                )}
                <div className="pt-4 border-t border-border">
                  <div className="flex justify-between items-end">
                    <div>
                      <p className="text-xs text-muted-foreground font-bold uppercase">Total Amount</p>
                      <p className="text-2xl font-bold text-primary">{formatCurrency(calculateTotal())}</p>
                    </div>
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-lg font-bold">NGN</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <button 
                  onClick={handleConfirm}
                  disabled={loading}
                  className="w-full bg-primary text-primary-foreground py-3.5 rounded-xl font-bold shadow-lg shadow-primary/20 hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                >
                  {loading && <Icon icon="lucide:loader-2" className="animate-spin" />}
                  {isEditMode ? 'Update Reservation' : 'Create Reservation'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
